import { SignupFormData, FormErrors } from '../types';

/**
 * Validates email format with more comprehensive regex
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates UK phone number format with comprehensive patterns
 */
export const isValidUKPhone = (phone: string): boolean => {
  // Remove all spaces, hyphens, and brackets
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // UK phone number patterns:
  // - Mobile: 07xxx xxxxxx (11 digits starting with 07)
  // - Landline: 01xxx xxxxxx, 02x xxxx xxxx, 03xxx xxxxxx (10-11 digits)
  // - International format: +44 or 0044 prefix
  
  // Check international format first (+44 or 0044)
  if (cleanPhone.startsWith('+44')) {
    const number = cleanPhone.substring(3);
    return /^[1-9]\d{8,9}$/.test(number);
  }
  
  if (cleanPhone.startsWith('0044')) {
    const number = cleanPhone.substring(4);
    return /^[1-9]\d{8,9}$/.test(number);
  }
  
  // Check UK domestic format (starting with 0)
  if (cleanPhone.startsWith('0')) {
    // Mobile numbers: 07xxx xxxxxx (11 digits total)
    if (cleanPhone.startsWith('07')) {
      return /^07\d{9}$/.test(cleanPhone);
    }
    
    // Landline numbers: 01xxx xxxxxx, 02x xxxx xxxx, 03xxx xxxxxx
    if (cleanPhone.startsWith('01') || cleanPhone.startsWith('02') || cleanPhone.startsWith('03')) {
      return /^0[123]\d{8,9}$/.test(cleanPhone);
    }
    
    // Other UK numbers (08xx, 09xx etc.)
    return /^0[8-9]\d{9}$/.test(cleanPhone);
  }
  
  return false;
};

/**
 * Validates FCA number format (6-8 digits)
 */
export const isValidFCANumber = (fcaNumber: string): boolean => {
  const cleanFCA = fcaNumber.replace(/\s+/g, '');
  return /^\d{6,8}$/.test(cleanFCA);
};

/**
 * Validates subdomain format for DNS
 */
export const isValidSubdomain = (subdomain: string): boolean => {
  const cleanSubdomain = subdomain.trim().toLowerCase();
  
  // Check length (3-63 characters)
  if (cleanSubdomain.length < 3 || cleanSubdomain.length > 63) {
    return false;
  }
  
  // Check format: only lowercase letters, numbers, and hyphens
  // Must start and end with alphanumeric character
  const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  
  // Check no consecutive hyphens
  return subdomainRegex.test(cleanSubdomain) && !cleanSubdomain.includes('--');
};

/**
 * Validates the entire form and returns errors
 */
export const validateForm = (formData: SignupFormData): FormErrors => {
  const errors: FormErrors = {};

  // First Name validation
  if (!formData.firstName.trim()) {
    errors.firstName = 'First name is required';
  } else if (formData.firstName.trim().length < 2) {
    errors.firstName = 'First name must be at least 2 characters';
  }

  // Last Name validation
  if (!formData.lastName.trim()) {
    errors.lastName = 'Last name is required';
  } else if (formData.lastName.trim().length < 2) {
    errors.lastName = 'Last name must be at least 2 characters';
  }

  // Email validation
  if (!formData.email.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Phone validation
  if (!formData.phone.trim()) {
    errors.phone = 'Phone number is required';
  } else if (!isValidUKPhone(formData.phone)) {
    errors.phone = 'Please enter a valid UK phone number (e.g., 07123456789 or +44 7123 456789)';
  }

  // FCA Number validation
  if (!formData.fcaNumber.trim()) {
    errors.fcaNumber = 'FCA number is required';
  } else if (!isValidFCANumber(formData.fcaNumber)) {
    errors.fcaNumber = 'Please enter a valid FCA number (6-8 digits)';
  }

  // Company Name validation
  if (!formData.companyName.trim()) {
    errors.companyName = 'Company name is required';
  } else if (formData.companyName.trim().length < 2) {
    errors.companyName = 'Company name must be at least 2 characters';
  }

  // Desired Subdomain validation
  if (!formData.desiredSubdomain.trim()) {
    errors.desiredSubdomain = 'Desired subdomain is required';
  } else if (!isValidSubdomain(formData.desiredSubdomain)) {
    errors.desiredSubdomain = 'Subdomain must be 3-63 characters, contain only lowercase letters, numbers, and hyphens';
  }

  // Job Title validation
  if (!formData.jobTitle.trim()) {
    errors.jobTitle = 'Job title is required';
  }

  // Company Size validation
  if (!formData.companySize) {
    errors.companySize = 'Please select your company size';
  }

  // Terms acceptance validation
  if (!formData.acceptTerms) {
    errors.acceptTerms = 'You must accept the terms and conditions';
  }

  // Note: trialTermsAccepted and privacyAccepted are not currently used in the form
  // They are included in the Terms and Conditions acceptance above

  return errors;
};

/**
 * Sanitizes form data by trimming whitespace
 */
export const sanitizeFormData = (formData: SignupFormData): SignupFormData => {
  return {
    ...formData,
    firstName: formData.firstName.trim(),
    lastName: formData.lastName.trim(),
    email: formData.email.trim().toLowerCase(),
    phone: formData.phone.trim(),
    fcaNumber: formData.fcaNumber.trim().replace(/\s+/g, ''),
    companyName: formData.companyName.trim(),
    companiesHouseNumber: formData.companiesHouseNumber.trim().toUpperCase(),
    desiredSubdomain: formData.desiredSubdomain.trim().toLowerCase(),
    jobTitle: formData.jobTitle.trim(),
  };
};

/**
 * Formats phone number for display
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  
  // Format UK numbers
  if (cleaned.startsWith('44')) {
    const national = cleaned.substring(2);
    return `+44 ${national.substring(0, 4)} ${national.substring(4, 7)} ${national.substring(7)}`.trim();
  }
  
  if (cleaned.startsWith('0')) {
    return `${cleaned.substring(0, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8)}`.trim();
  }
  
  return phone;
};

/**
 * Formats FCA number for display
 */
export const formatFCANumber = (fcaNumber: string): string => {
  const cleaned = fcaNumber.replace(/\D/g, '');
  
  // Add spacing for readability (e.g., 123456 -> 123 456)
  if (cleaned.length >= 6) {
    return cleaned.substring(0, 3) + ' ' + cleaned.substring(3);
  }
  
  return cleaned;
};

/**
 * Debounce function for input validation
 */
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Capitalizes the first letter of each word
 */
export const capitalizeWords = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Generates a random ID for form elements
 */
export const generateId = (prefix: string = 'form'): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Checks if the current environment is development
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Safe JSON parse with fallback
 */
export const safeJsonParse = <T>(json: string, fallback: T): T => {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
};

/**
 * Creates a delay for testing/demo purposes
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Formats error messages for display
 */
export const formatErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
};

const validationUtils = {
  isValidEmail,
  isValidUKPhone,
  isValidFCANumber,
  validateForm,
  sanitizeFormData,
  formatPhoneNumber,
  formatFCANumber,
  debounce,
  capitalizeWords,
  generateId,
  isDevelopment,
  safeJsonParse,
  delay,
  formatErrorMessage,
};

export default validationUtils;
